import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { getRewards, createReward, deleteReward, redeemReward, getRedemptions, getPointsBalance, extractErrorMessage } from '../services/api';
import { FaPlus, FaTrash, FaGift, FaShoppingCart } from 'react-icons/fa';
import { useToast } from '../components/Toast';
import '../styles/Rewards.css';

function Rewards({ user }) {
  const toast = useToast();
  const [rewards, setRewards] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('shop');
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [newReward, setNewReward] = useState({
    name: '',
    description: '',
    points_cost: 0,
    category: 'small'
  });

  useEffect(() => {
    fetchRewards();
    fetchRedemptions();
    fetchBalance();
  }, []);

  const fetchRewards = async () => {
    try {
      setLoading(true);
      const data = await getRewards();
      setRewards(data.rewards);
    } catch (error) {
      console.error('Error fetching rewards:', error);
      toast.error(extractErrorMessage(error, 'Belohnungen konnten nicht geladen werden'));
    } finally {
      setLoading(false);
    }
  };

  const fetchRedemptions = async () => {
    try {
      const data = await getRedemptions({ user_id: user.id });
      setRedemptions(data.redemptions);
    } catch (error) {
      console.error('Error fetching redemptions:', error);
      toast.error(extractErrorMessage(error, 'Einlösungen konnten nicht geladen werden'));
    }
  };

  const fetchBalance = async () => {
    try {
      const data = await getPointsBalance(user.id);
      setBalance(data.balance);
    } catch (error) {
      console.error('Error fetching balance:', error);
      toast.error(extractErrorMessage(error, 'Punktestand konnte nicht geladen werden'));
    }
  };

  const handleCreateReward = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setFormError(null);
    try {
      await createReward(newReward);
      setNewReward({ name: '', description: '', points_cost: 0, category: 'small' });
      setShowModal(false);
      toast.success('Belohnung erstellt');
      fetchRewards();
    } catch (error) {
      console.error('Error creating reward:', error);
      setFormError(extractErrorMessage(error, 'Fehler beim Erstellen der Belohnung'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteReward = async (id) => {
    if (!window.confirm('Belohnung wirklich deaktivieren?')) return;
    try {
      await deleteReward(id);
      fetchRewards();
    } catch (error) {
      console.error('Error deleting reward:', error);
    }
  };

  const handleRedeem = async (reward) => {
    if (balance < reward.points_cost) {
      toast.warning(`Nicht genug Punkte! Benötigt: ${reward.points_cost}, Vorhanden: ${balance}`);
      return;
    }
    if (!window.confirm(`${reward.name} für ${reward.points_cost} Punkte einlösen?`)) return;
    try {
      await redeemReward({ user_id: user.id, reward_id: reward.id });
      fetchBalance();
      fetchRedemptions();
      toast.success('Belohnung eingelöst');
    } catch (error) {
      console.error('Error redeeming reward:', error);
      toast.error(extractErrorMessage(error, 'Fehler beim Einlösen der Belohnung'));
    }
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
    setFormError(null);
  };

  const getCategoryLabel = (category) => {
    const labels = {
      small: 'Klein',
      medium: 'Mittel',
      large: 'Groß'
    };
    return labels[category] || category;
  };

  const groupedRewards = rewards.reduce((acc, reward) => {
    if (!acc[reward.category]) acc[reward.category] = [];
    acc[reward.category].push(reward);
    return acc;
  }, {});

  return (
    <div className="app-layout">
      <Navbar user={user} />
      <main className="main-content">
        <div className="page-header">
          <h1>Belohnungen</h1>
          <div className="balance-display">
            <span className="balance-label">Dein Punktestand:</span>
            <span className="balance-value">{balance} Punkte</span>
          </div>
          {user.isAdmin && (
            <button className="add-btn" onClick={() => setShowModal(true)}>
              <FaPlus /> Neue Belohnung
            </button>
          )}
        </div>

        <div className="tab-buttons">
          <button className={activeTab === 'shop' ? 'active' : ''} onClick={() => setActiveTab('shop')}>
            <FaShoppingCart /> Shop
          </button>
          <button className={activeTab === 'history' ? 'active' : ''} onClick={() => setActiveTab('history')}>
            <FaGift /> Meine Belohnungen ({redemptions.length})
          </button>
        </div>

        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        ) : activeTab === 'shop' ? (
          <div className="rewards-shop">
            {['small', 'medium', 'large'].map(category => {
              const categoryRewards = groupedRewards[category] || [];
              if (categoryRewards.length === 0) return null;
              return (
                <div key={category} className="reward-category">
                  <h2 className="category-title">{getCategoryLabel(category)}e Belohnungen</h2>
                  <div className="rewards-grid">
                    {categoryRewards.map(reward => (
                      <div key={reward.id} className={`reward-card ${balance >= reward.points_cost ? 'affordable' : 'expensive'}`}>
                        <div className="reward-content">
                          <h3 className="reward-name">{reward.name}</h3>
                          {reward.description && <p className="reward-desc">{reward.description}</p>}
                        </div>
                        <div className="reward-footer">
                          <span className="reward-cost">{reward.points_cost} Punkte</span>
                          <div className="reward-actions">
                            <button
                              className="redeem-btn"
                              onClick={() => handleRedeem(reward)}
                              disabled={balance < reward.points_cost}
                            >
                              Einlösen
                            </button>
                            {user.isAdmin && (
                              <button className="delete-btn" onClick={() => handleDeleteReward(reward.id)}>
                                <FaTrash />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            {rewards.length === 0 && <div className="no-data">Keine Belohnungen im Shop</div>}
          </div>
        ) : (
          <div className="redemptions-list">
            {redemptions.length > 0 ? (
              <div className="redemptions-grid">
                {redemptions.map(r => (
                  <div key={r.id} className={`redemption-card ${r.status}`}>
                    <div className="redemption-status">
                      {r.status === 'fulfilled' ? 'Erfüllt' : 'Ausstehend'}
                    </div>
                    <h3 className="redemption-name">{r.name}</h3>
                    <div className="redemption-meta">
                      <span className="redemption-cost">{r.points_cost} Punkte</span>
                      <span className="redemption-date">{new Date(r.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-data">Noch keine Belohnungen eingelöst</div>
            )}
          </div>
        )}

        {showModal && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h2>Neue Belohnung</h2>
              {formError && <div className="shared-form-error-banner">{formError}</div>}
              <form onSubmit={handleCreateReward}>
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={newReward.name}
                    onChange={e => setNewReward({...newReward, name: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Beschreibung</label>
                  <textarea
                    value={newReward.description}
                    onChange={e => setNewReward({...newReward, description: e.target.value})}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Kategorie</label>
                    <select
                      value={newReward.category}
                      onChange={e => setNewReward({...newReward, category: e.target.value})}
                    >
                      <option value="small">Klein</option>
                      <option value="medium">Mittel</option>
                      <option value="large">Groß</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Kosten (Punkte) *</label>
                    <input
                      type="number"
                      value={newReward.points_cost}
                      onChange={e => setNewReward({...newReward, points_cost: parseInt(e.target.value) || 0})}
                      required
                      min="1"
                    />
                  </div>
                </div>
                <div className="modal-actions">
                  <button type="button" className="cancel-btn" onClick={closeModal} disabled={saving}>Abbrechen</button>
                  <button type="submit" className="submit-btn" disabled={saving}>{saving ? 'Speichert…' : 'Erstellen'}</button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Rewards;
